import { useHashParam } from "hooks/use-hash-param"
import { isInertiaPage, isReactRbPage } from "core/navigation"

export const SUBSCRIBER_PROFILE_ID_PARAM = "subscriber_profile_id"
export const SUBSCRIBER_PROFILE_TAB_PARAM = "subscriber_profile_tab"

/**
 SubscriberProfileDrawer is mounted only in AppWrapper component, which is rendered for Inertia and react-rb pages.
 */
export function isSubscriberProfileDrawerMounted() {
  return isInertiaPage() || isReactRbPage()
}

/**
 * Tracks the `subscriber_profile_id` hash param.
 *
 * A hash param, not a query param: `hashchange` is a distinct browser event
 * from `popstate`, so it's unaffected by the app-wide `popstate` handler in
 * `entrypoints/inertia.js` that blocks every other `popstate` listener on
 * `window` (see that file's comment). That handler is intentional, existing
 * behavior for the `UnsavedChanges` feature — not something this feature
 * should work around globally — and a hash param sidesteps it entirely
 * rather than needing to.
 *
 * Single global instance (mounted once in `app-wrapper.jsx`), so reusing
 * `useHashParam` as-is is fine here — no per-row hook-instance concerns like
 * `setSubscriberProfileId` below has.
 */
export function useSubscriberProfileId() {
  const [subscriberProfileId] = useHashParam(SUBSCRIBER_PROFILE_ID_PARAM)
  return subscriberProfileId
}

/**
 * Tracks the `subscriber_profile_tab` hash param — which activity tab
 * (see ProfileDrawerActivities) the drawer should open to. Absent unless a
 * caller opened the drawer with an explicit `tab` via `setSubscriberProfileId`.
 */
export function useSubscriberProfileTab() {
  const [subscriberProfileTab] = useHashParam(SUBSCRIBER_PROFILE_TAB_PARAM)
  return subscriberProfileTab
}

/**
 * Sets (or clears, when `subscriberId` is falsy) the `subscriber_profile_id`
 * hash param.
 *
 * Not implemented via `useHashParam`'s own setter: this is called from
 * `SubscriberProfileDrawerLink`, rendered once per row, and routing the
 * setter through the hook would mean every row mounts its own unused
 * `useHashParam` state + `hashchange` listener just to reach a setter it
 * never reads from. A plain function avoids that.
 *
 * Opening pushes a new history entry, so the browser Back button closes the
 * drawer. Closing replaces the current entry instead — pushing here would
 * leave the "open" entry sitting one step behind the "closed" one, so Back
 * would re-open the drawer instead of leaving the page, and every open/close
 * cycle would add two entries instead of one.
 *
 * Both branches go through `history.pushState`/`replaceState` rather than a
 * plain `location.hash` assignment for the open case — unlike those two,
 * assigning `location.hash` fires a real `popstate` event, which the
 * app-wide `popstate` handler in `entrypoints/inertia.js` treats as back/
 * forward navigation and answers with `router.reload()`, refetching the
 * current Inertia page's props from the server on every open. `pushState`
 * still adds the history entry Back needs, without that side effect.
 *
 * Neither history method fires `hashchange` on its own, so it's dispatched
 * manually for both — this also makes cross-component propagation (a table
 * row click updating the globally-mounted drawer) deterministic rather than
 * relying on the browser's own (absent, for pushState/replaceState) dispatch.
 *
 * `tab` opens the drawer straight to a given activity tab (see
 * ProfileDrawerActivities) instead of always starting on Emails — e.g. a
 * link from a purchases-related page can open directly to Purchases. It's
 * set/cleared on the same params object as the id, in the same history
 * entry, rather than through a second, independent hash write (which would
 * reintroduce the raw-`location.hash` popstate/reload problem above) — and
 * it's always explicitly set-or-deleted on open so a previous open's tab
 * never leaks into one that doesn't ask for it.
 */
export function setSubscriberProfileId(subscriberId, { tab } = {}) {
  const params = new URLSearchParams(window.location.hash.slice(1))
  const normalizedId = subscriberId ? String(subscriberId) : null

  if (normalizedId) {
    params.set(SUBSCRIBER_PROFILE_ID_PARAM, normalizedId)
    if (tab) {
      params.set(SUBSCRIBER_PROFILE_TAB_PARAM, tab)
    } else {
      params.delete(SUBSCRIBER_PROFILE_TAB_PARAM)
    }
  } else {
    params.delete(SUBSCRIBER_PROFILE_ID_PARAM)
    params.delete(SUBSCRIBER_PROFILE_TAB_PARAM)
  }

  const oldURL = window.location.href
  const url = new URL(window.location.href)
  url.hash = params.toString()

  if (normalizedId) {
    window.history.pushState(window.history.state, "", url)
  } else {
    window.history.replaceState(window.history.state, "", url)
  }

  window.dispatchEvent(
    new HashChangeEvent("hashchange", { oldURL, newURL: url.href })
  )
}

/**
 * Updates the `subscriber_profile_tab` hash param while the drawer is
 * already open (e.g. the subscriber switches activity tabs), leaving the
 * `subscriber_profile_id` param untouched.
 *
 * Always goes through `history.replaceState`, never `pushState` — unlike
 * opening the drawer, switching tabs shouldn't add a history entry, or
 * clicking through a few tabs would mean pressing Back that many times
 * before it even started closing the drawer.
 */
export function setSubscriberProfileTab(tab) {
  const params = new URLSearchParams(window.location.hash.slice(1))

  if (tab) {
    params.set(SUBSCRIBER_PROFILE_TAB_PARAM, tab)
  } else {
    params.delete(SUBSCRIBER_PROFILE_TAB_PARAM)
  }

  const oldURL = window.location.href
  const url = new URL(window.location.href)
  url.hash = params.toString()

  window.history.replaceState(window.history.state, "", url)

  window.dispatchEvent(
    new HashChangeEvent("hashchange", { oldURL, newURL: url.href })
  )
}
